/**
 * AI Agent Audit API Client
 *
 * Customer-facing calls to the AI Agent API (via gateway /api/agent/v1).
 * Covers:
 *   - Site audit:  POST/GET /audit/site
 *   - Page audit:  POST/GET /audit/request(s)
 */

import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import config from '../../config';
import { applySourceSystemHeader } from '../../utils/sourceSystemHeaders';

const AI_AGENT_BASE_URL = config.VITE_AI_AGENT_API_BASE_URL;

const auditApi = axios.create({
  baseURL: AI_AGENT_BASE_URL,
  headers: { 'Content-Type': 'application/json', sourceSystem: 'CUSTOMER_PORTAL' },
});

// Inject Bearer + X-Tenant-Id + X-User-Id on every request
auditApi.interceptors.request.use((cfg) => {
  const { token, user } = useAuthStore.getState();
  const headers = cfg.headers ?? {};
  applySourceSystemHeader(headers as unknown as Record<string, unknown>);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Use effective tenant (proxy if switched, else home)
  const effectiveTenantId = user?.proxyTenantId || user?.tenantId;
  if (effectiveTenantId) headers['X-Tenant-Id'] = effectiveTenantId;
  if (user?.id) headers['X-User-Id'] = user.id;
  return cfg;
});

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface SiteAuditRun {
  id: number;
  siteId: number;
  siteUrl: string;
  /** PENDING | DISPATCHING | RUNNING | COMPLETED | FAILED */
  status: 'PENDING' | 'DISPATCHING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  totalPages: number;
  completedPages: number;
  failedPages: number;
  pendingPages: number;
  maxPages: number;
  llmEnabled: boolean;
  requestedBy: string | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface SiteAuditPage {
  requestId: number;
  pageUrl: string;
  status: string;
  runId: string | null;
  recommendationCount: number;
  completedAt: string | null;
  createdAt: string;
}

export interface PageAuditRequest {
  id: number;
  tenant_id: string;
  site_id: number;
  page_url: string | null;
  /** QUEUED | PROCESSING | COMPLETED | FAILED | CANCELLED */
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  run_id: string | null;
  llm_enabled: boolean | null;
  priority: number | null;
  requested_by: string | null;
  created_at: string;
  processing_started_at: string | null;
  completed_at: string | null;
  retry_count: number | null;
  error_message: string | null;
  recommendation_count: number | null;
}

interface SpringPage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

/** Unwrap ApiResponse<T> envelope from Spring Boot */
function unwrap<T>(response: { data: unknown }): T {
  const d = (response.data as { data?: T });
  return d?.data !== undefined ? d.data : (response.data as T);
}

// ─── Site Audit ───────────────────────────────────────────────────────────────

/** Start a new site audit for the authenticated tenant's site. */
export async function startSiteAudit(params: {
  siteId: number;
  maxPages?: number;
  llmEnabled?: boolean;
}): Promise<SiteAuditRun> {
  const res = await auditApi.post('/audit/site', {
    siteId: params.siteId,
    maxPages: params.maxPages ?? 50,
    llmEnabled: params.llmEnabled ?? true,
  });
  return unwrap<SiteAuditRun>(res);
}

/** List site audit runs for the tenant (optionally scoped to one siteId). */
export async function listSiteAuditRuns(params?: {
  siteId?: number;
  page?: number;
  size?: number;
}): Promise<{ data: SiteAuditRun[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.siteId) q.set('siteId', String(params.siteId));
  q.set('page', String((params?.page ?? 1) - 1));
  q.set('size', String(params?.size ?? 20));
  const res = await auditApi.get(`/audit/site?${q}`);
  const page = unwrap<SpringPage<SiteAuditRun>>(res);
  return { data: page.content ?? [], total: page.totalElements ?? 0 };
}

/** Poll a single site audit run. */
export async function getSiteAuditRun(runId: number): Promise<SiteAuditRun> {
  const res = await auditApi.get(`/audit/site/${runId}`);
  return unwrap<SiteAuditRun>(res);
}

/** Per-page status breakdown for a site audit run. */
export async function getSiteAuditPages(
  runId: number,
  page = 0,
  size = 50
): Promise<{ data: SiteAuditPage[]; total: number }> {
  const res = await auditApi.get(`/audit/site/${runId}/pages?page=${page}&size=${size}`);
  const sp = unwrap<SpringPage<SiteAuditPage>>(res);
  return { data: sp.content ?? [], total: sp.totalElements ?? 0 };
}

// ─── Page Audit ───────────────────────────────────────────────────────────────

/** Submit a single-page (or full-site) audit request. Returns immediately. */
export async function submitPageAudit(params: {
  siteId: number;
  pageUrl?: string;
  targetQueries?: string[];
  llmEnabled?: boolean;
  priority?: number;
}): Promise<PageAuditRequest> {
  const res = await auditApi.post('/audit/request', {
    site_id: params.siteId,
    page_url: params.pageUrl ?? null,
    target_queries: params.targetQueries ?? null,
    llm_enabled: params.llmEnabled ?? true,
    priority: params.priority ?? 5,
  });
  return unwrap<PageAuditRequest>(res);
}

/** Poll the status of a page audit request. */
export async function getPageAuditStatus(id: number): Promise<PageAuditRequest> {
  const res = await auditApi.get(`/audit/request/${id}`);
  return unwrap<PageAuditRequest>(res);
}

/** List page audit requests for the tenant. */
export async function listPageAuditRequests(params?: {
  status?: string;
  page?: number;
  size?: number;
}): Promise<{ data: PageAuditRequest[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  q.set('page', String((params?.page ?? 1) - 1));
  q.set('size', String(params?.size ?? 20));
  const res = await auditApi.get(`/audit/requests?${q}`);
  const sp = unwrap<SpringPage<PageAuditRequest>>(res);
  return { data: sp.content ?? [], total: sp.totalElements ?? 0 };
}

// ─── Recommendations ─────────────────────────────────────────────────────────

export interface AuditRecommendation {
  id: number;
  tenantId: string;
  siteId: number;
  runId: string;
  recommendationType: string;
  agent: string;
  title: string;
  issue: string;
  priority: string | null;
  impact: string | null;
  effort: string | null;
  confidence: number | null;
  riskLevel: string | null;
  targetUrl: string | null;
  targetQuery: string | null;
  recommendationData: Record<string, unknown> | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/** Fetch AI-generated recommendations for a completed audit run (scoped to tenant). */
export async function getAuditRecommendations(
  runId: string
): Promise<{ data: AuditRecommendation[]; total: number }> {
  const res = await auditApi.get(
    `/audit/recommendations?runId=${encodeURIComponent(runId)}&size=50`
  );
  const sp = unwrap<SpringPage<AuditRecommendation>>(res);
  return { data: sp.content ?? [], total: sp.totalElements ?? 0 };
}

/** Customer ignores an admin-approved recommendation that is not relevant to their site. */
export async function rejectRecommendation(id: number, reason?: string): Promise<void> {
  await auditApi.post(`/audit/recommendations/${id}/reject`, { notes: reason ?? '' });
}

/** Customer marks a recommendation as applied (they implemented the change). */
export async function applyRecommendation(id: number): Promise<void> {
  await auditApi.post(`/audit/recommendations/${id}/apply`);
}
